import type { DiffEntry, InvoiceVersion } from 'payments-api/model';
import { useGetInvoicesIdHistorySuspense } from 'api/payments/generated/fragmentPaymentsAPI';
import React, { Suspense } from 'react';

const DiffEntryRow = ({ entry }: { entry: DiffEntry }) => {
  if ('old_amount' in entry) {
    return (
      <div className="text-main-500 text-xmd font-FragmentMono">
        ~ {entry.id.slice(0, 8)} {entry.old_amount} → {entry.new_amount}
      </div>
    );
  }
  const isAdd = String(entry.op).includes('add');
  return (
    <div className="text-main text-xmd font-FragmentMono">
      {isAdd ? '+' : '−'} {entry.item.product_id} · {entry.item.user_id} ·{' '}
      {entry.item.amount} {entry.item.currency_code}
    </div>
  );
};

const VersionEntry = ({ version }: { version: InvoiceVersion }) => {
  const date = version.modified ?? version.created;
  const label = new Date(date).toDateString().split(' ').slice(1).join(' ');
  return (
    <div className="flex flex-row gap-x-f1 p-f1 border-b border-main-200 last:border-b-0">
      <span className="text-main-500 text-xmd w-1/3 shrink-0">{label}</span>
      <div className="flex flex-col gap-y-0.5 w-2/3">
        {version.diff && version.diff.length > 0 ? (
          version.diff.map((entry, i) => <DiffEntryRow key={i} entry={entry} />)
        ) : (
          <span className="text-main-500 text-xmd">Initial version</span>
        )}
      </div>
    </div>
  );
};

const InvoiceHistoryPanelContent = ({ invoiceId }: { invoiceId: string }) => {
  const { data } = useGetInvoicesIdHistorySuspense(invoiceId);
  const versions = [...data.data].reverse();

  return (
    <div className="flex flex-col overflow-y-auto h-full">
      {versions.length === 0 ? (
        <div className="text-main-500 text-xmd p-f1">No history yet.</div>
      ) : (
        versions.map((version) => (
          <VersionEntry key={version.version} version={version} />
        ))
      )}
    </div>
  );
};

export const InvoiceHistoryPanel = ({ invoiceId }: { invoiceId: string }) => (
  <div className="flex flex-col h-full">
    <div className="text-main text-xmd p-f1 border-b border-main-200">
      History
    </div>
    <Suspense
      fallback={
        <div className="text-main-500 text-xmd p-f1">Loading history...</div>
      }
    >
      <InvoiceHistoryPanelContent invoiceId={invoiceId} />
    </Suspense>
  </div>
);
